/*
 * Quick View - Lockhone
 *
 * Extends the theme's ModalDialog element, so Escape-to-close, backdrop click,
 * focus trapping, focus restore and body scroll locking are inherited from the
 * theme rather than reimplemented.
 *
 * Content is loaded on demand through the Section Rendering API:
 *   /products/{handle}?section_id=quick-view
 *
 * Add to cart reuses the theme's <product-form> element, so the normal AJAX
 * cart path (cart drawer / cart notification) is untouched.
 */
if (!customElements.get('quick-view-modal')) {
  customElements.define(
    'quick-view-modal',
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector('#QuickViewInfo');
        this.container = null;
        this.variants = [];
        this.slides = [];
        this.dots = [];
        this.optionInputs = [];
        this.currentSlide = 0;
        this.abortController = null;
        this.scrollTimeout = null;

        // Close the popup once the item is in the cart so the cart drawer,
        // which the theme opens straight after, is not stacked behind us.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!this.hasAttribute('open')) return;
            if (!event || event.source !== 'product-form') return;
            this.hide(true);
          });
        }
      }

      connectedCallback() {
        // A product grid re-rendered by filtering or pagination can ship a
        // second copy of the shell. Keep the first, discard any duplicates.
        const primary = document.querySelector('quick-view-modal[data-qv-primary]');
        if (primary && primary !== this) {
          this.remove();
          return;
        }

        this.setAttribute('data-qv-primary', '');
        super.connectedCallback();
      }

      show(opener) {
        const productUrl = opener.dataset.productUrl;
        if (!productUrl || !this.modalContent) return;

        this.setLoading(opener, true);

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const separator = productUrl.indexOf('?') > -1 ? '&' : '?';

        fetch(`${productUrl}${separator}section_id=quick-view`, {
          signal: this.abortController.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const parsed = new DOMParser().parseFromString(responseText, 'text/html');
            const content = parsed.querySelector('[data-quick-view]');
            if (!content) throw new Error('Quick view markup not found in section response');

            HTMLUpdateUtility.setInnerHTML(this.modalContent, content.outerHTML);
            this.initContent();
            super.show(opener);
            this.setLoading(opener, false);
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error('[quick-view]', error);
            this.setLoading(opener, false);
            // Never trap the shopper: fall back to the full product page.
            window.location.href = productUrl;
          });
      }

      hide(preventFocus = false) {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && this.openedBy && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(this.openedBy);
        }

        if (preventFocus) this.openedBy = null;

        super.hide();

        if (this.modalContent) this.modalContent.innerHTML = '';
        this.container = null;
        this.variants = [];
        this.slides = [];
        this.dots = [];
        this.optionInputs = [];
        this.currentSlide = 0;
      }

      setLoading(opener, isLoading) {
        const spinner = opener.querySelector('.loading__spinner');

        if (isLoading) {
          opener.setAttribute('aria-disabled', 'true');
          opener.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      initContent() {
        this.container = this.modalContent.querySelector('[data-quick-view]');
        if (!this.container) return;

        const dataElement = this.container.querySelector('[data-quick-view-data]');
        if (dataElement) {
          try {
            const parsed = JSON.parse(dataElement.textContent);
            this.variants = parsed.variants || [];
          } catch (error) {
            console.error('[quick-view] could not parse variant data', error);
            this.variants = [];
          }
        }

        this.initGallery();
        this.initOptions();
      }

      /* ----------------------------------------------------------------- */
      /* Gallery                                                            */
      /* ----------------------------------------------------------------- */

      initGallery() {
        this.track = this.container.querySelector('[data-qv-track]');
        if (!this.track) return;

        this.slides = Array.from(this.track.querySelectorAll('[data-qv-slide]'));
        this.dots = Array.from(this.container.querySelectorAll('[data-qv-dot]'));
        this.currentSlide = 0;

        const prev = this.container.querySelector('[data-qv-prev]');
        const next = this.container.querySelector('[data-qv-next]');

        if (prev) prev.addEventListener('click', () => this.goToSlide(this.currentSlide - 1));
        if (next) next.addEventListener('click', () => this.goToSlide(this.currentSlide + 1));

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.index, 10)));
        });

        this.track.addEventListener('scroll', this.onGalleryScroll.bind(this), { passive: true });
        this.updateGalleryUI();
      }

      onGalleryScroll() {
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          if (!this.track || !this.track.clientWidth) return;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
          if (clamped !== this.currentSlide) {
            this.currentSlide = clamped;
            this.updateGalleryUI();
          }
        }, 80);
      }

      goToSlide(index, behavior = 'smooth') {
        if (!this.slides.length || !this.track) return;

        this.currentSlide = Math.max(0, Math.min(index, this.slides.length - 1));
        this.track.scrollTo({
          left: this.track.clientWidth * this.currentSlide,
          behavior,
        });
        this.updateGalleryUI();
      }

      updateGalleryUI() {
        if (!this.container) return;

        const prev = this.container.querySelector('[data-qv-prev]');
        const next = this.container.querySelector('[data-qv-next]');

        if (prev) prev.disabled = this.currentSlide === 0;
        if (next) next.disabled = this.currentSlide >= this.slides.length - 1;

        this.dots.forEach((dot, index) => {
          if (index === this.currentSlide) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      /* ----------------------------------------------------------------- */
      /* Variants                                                           */
      /* ----------------------------------------------------------------- */

      initOptions() {
        this.optionInputs = Array.from(this.container.querySelectorAll('[data-qv-option]'));
        if (!this.optionInputs.length) return;

        this.optionInputs.forEach((input) => {
          input.addEventListener('change', () => this.onOptionChange());
        });

        this.onOptionChange(true);
      }

      getSelectedOptions() {
        const selected = [];
        this.optionInputs.forEach((input) => {
          if (!input.checked) return;
          selected[parseInt(input.dataset.qvOptionPosition, 10) - 1] = input.value;
        });
        return selected;
      }

      findVariant(options) {
        return this.variants.find((variant) =>
          variant.options.every((value, index) => value === options[index])
        );
      }

      onOptionChange(isInitial = false) {
        const selected = this.getSelectedOptions();
        const variant = this.findVariant(selected);

        this.updateOptionStates(selected);
        this.updatePrice(variant);
        this.updateSubmit(variant);
        this.updateQuantityRules(variant);
        this.updateGalleryForVariant(variant, isInitial ? 'auto' : 'smooth');

        if (!isInitial) this.announce(variant);
      }

      updateOptionStates(selected) {
        this.optionInputs.forEach((input) => {
          const position = parseInt(input.dataset.qvOptionPosition, 10) - 1;
          const trial = selected.slice();
          trial[position] = input.value;

          const match = this.findVariant(trial);
          input.classList.toggle('disabled', !match || !match.available);
        });
      }

      updatePrice(variant) {
        const priceElement = this.container.querySelector('[data-qv-price]');
        if (!priceElement) return;

        if (!variant) {
          priceElement.classList.add('price--unavailable');
          return;
        }

        priceElement.classList.remove('price--unavailable');

        const regular = priceElement.querySelector('[data-qv-price-regular]');
        const sale = priceElement.querySelector('[data-qv-price-sale]');
        const compare = priceElement.querySelector('[data-qv-price-compare]');

        if (regular) regular.textContent = variant.price;
        if (sale) sale.textContent = variant.price;
        if (compare) compare.textContent = variant.compare_at_price || '';

        priceElement.classList.toggle('price--on-sale', Boolean(variant.compare_at_price));
        priceElement.classList.toggle('price--sold-out', !variant.available);
      }

      updateSubmit(variant) {
        const variantInput = this.container.querySelector('[data-qv-variant-id]');
        const submit = this.container.querySelector('[data-qv-submit]');
        const label = this.container.querySelector('[data-qv-submit-text]');
        const strings = window.variantStrings || {};

        if (variantInput) variantInput.value = variant ? variant.id : '';
        if (!submit) return;

        if (!variant) {
          submit.setAttribute('disabled', 'disabled');
          if (label) label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          submit.setAttribute('disabled', 'disabled');
          if (label) label.textContent = strings.soldOut || 'Sold out';
        } else {
          submit.removeAttribute('disabled');
          if (label) label.textContent = strings.addToCart || 'Add to cart';
        }
      }

      updateQuantityRules(variant) {
        const input = this.container.querySelector('[data-qv-quantity]');
        if (!input || !variant) return;

        input.min = variant.qty_min || 1;
        input.step = variant.qty_step || 1;

        if (variant.qty_max) input.max = variant.qty_max;
        else input.removeAttribute('max');

        if (parseInt(input.value, 10) < parseInt(input.min, 10)) input.value = input.min;

        const quantityInput = input.closest('quantity-input');
        if (quantityInput && typeof quantityInput.validateQtyRules === 'function') {
          quantityInput.validateQtyRules();
        }
      }

      updateGalleryForVariant(variant, behavior) {
        if (!variant || !variant.media_id || !this.slides.length) return;

        const index = this.slides.findIndex(
          (slide) => slide.dataset.mediaId === String(variant.media_id)
        );
        if (index > -1 && index !== this.currentSlide) this.goToSlide(index, behavior);
      }

      announce(variant) {
        const status = this.container.querySelector('[data-qv-status]');
        if (!status) return;

        const strings = window.variantStrings || {};

        if (!variant) status.textContent = strings.unavailable || 'Unavailable';
        else if (!variant.available) status.textContent = strings.soldOut || 'Sold out';
        else status.textContent = `${variant.options.join(', ')}. ${variant.price}`;
      }
    }
  );
}
